/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Sigil-aware replace-range widening for completion candidates whose label
 * keeps a leading sigil (`@local`, `@@spid`, `:bind`, `$1`, `#temp`).
 *
 * Monaco's SQL word-definition treats sigils as word separators, so
 * `model.getWordUntilPosition` at `@v_|` returns the word `v_` with its
 * `startColumn` at the `v` — the `@` is excluded. When the resolver emits a
 * candidate whose `filterText`/`label` keeps the sigil (`@v_round2…`), Monaco's
 * fuzzy filter scores the typed word `v_` against `@v_round2…` with
 * `firstMatchCanBeWeak: false`: the matched `v` sits immediately after `@`,
 * which Monaco does NOT treat as a separator, so the first match is "weak" and
 * the entire item is scored `undefined` and dropped. The suggest widget then
 * renders empty even though the resolver returned the variable.
 *
 * Widening the replace range back over the contiguous sigil run makes the typed
 * word `@v_`, giving the sigil-prefixed candidate a strong prefix match. The
 * widening is applied per-item (only to candidates whose own label starts with
 * a sigil) so non-sigil candidates in the same batch keep their normal range
 * and still insert correctly.
 */

const SIGIL_CHARS = new Set(['@', ':', '$', '#']);

/**
 * Returns a widened `startColumn` (1-based) that extends back over a contiguous
 * run of sigil characters immediately preceding `wordStartColumn`, or `null`
 * when no sigil precedes the word (no widening needed).
 */
export function sigilWidenedStartColumn(
  lineContent: string,
  wordStartColumn: number,
): number | null {
  let widened = wordStartColumn;
  // `widened` is 1-based; the character immediately before it lives at string
  // index `widened - 2`.
  while (widened > 1 && SIGIL_CHARS.has(lineContent.charAt(widened - 2))) {
    widened -= 1;
  }
  return widened < wordStartColumn ? widened : null;
}

/** True when the candidate's display label begins with a sigil character. */
export function labelStartsWithSigil(item: { label: unknown }): boolean {
  const raw =
    typeof item.label === 'string'
      ? item.label
      : (item.label as { label?: string } | null)?.label;
  return typeof raw === 'string' && raw.length > 0 && SIGIL_CHARS.has(raw.charAt(0));
}

/**
 * Build the per-item range used when mapping ranked candidates to Monaco items.
 * Sigil-prefixed candidates get the widened range (so Monaco's fuzzy filter
 * keeps them once the user types past the sigil); everything else keeps the
 * base range. Returns a selector function to call inside the `.map`.
 */
export function makeSigilAwareRangeSelector(
  model: { getLineContent: (line: number) => string },
  lineNumber: number,
  baseRange: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number },
): (item: { label: unknown }) => typeof baseRange {
  let widenedStart: number | null = null;
  try {
    widenedStart = sigilWidenedStartColumn(model.getLineContent(lineNumber), baseRange.startColumn);
  } catch {
    widenedStart = null;
  }
  if (widenedStart === null) return () => baseRange;
  const widenedRange = { ...baseRange, startColumn: widenedStart };
  return (item) => (labelStartsWithSigil(item) ? widenedRange : baseRange);
}
